/**
 * CertificationsEditor — admin maintenance for a product's safety
 * certifications (Slice A4). Pick a product, toggle standardized certification
 * codes as chips, and save via the field-level PATCH endpoint (NOT the full
 * product PUT). The allowed code list comes from the backend so the UI can
 * never persist a code the server would reject.
 *
 * Placement: a subtab of the AI Management area (mirrors the AI Flows subtab
 * pattern) — the lightest coherent host, since there is no dedicated product-
 * admin UI. All UI text is English.
 */
import { useEffect, useMemo, useState } from 'preact/hooks'

import {
  getProductCertifications,
  listAdminProducts,
  updateProductCertifications,
} from '../../services/adminService.js'

function productLabel(p) {
  const parts = [p.manufacturer, p.name].filter(Boolean).join(' ')
  return p.b_num ? `${p.b_num} — ${parts}` : parts || p.product_id
}

export function CertificationsEditor() {
  const [products, setProducts] = useState([])
  const [loadingProducts, setLoadingProducts] = useState(true)
  const [productError, setProductError] = useState(null)
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState(null)

  const [allowed, setAllowed] = useState([])
  const [selected, setSelected] = useState([])
  const [loadingCerts, setLoadingCerts] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [savedAt, setSavedAt] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoadingProducts(true)
    listAdminProducts()
      .then((data) => {
        if (cancelled) return
        setProducts(data.products || [])
      })
      .catch((e) => !cancelled && setProductError(e.message))
      .finally(() => !cancelled && setLoadingProducts(false))
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!selectedId) {
      setAllowed([])
      setSelected([])
      return
    }
    let cancelled = false
    setLoadingCerts(true)
    setError(null)
    setSavedAt(null)
    getProductCertifications(selectedId)
      .then((data) => {
        if (cancelled) return
        setAllowed(data.allowed || [])
        setSelected(data.certifications || [])
      })
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoadingCerts(false))
    return () => {
      cancelled = true
    }
  }, [selectedId])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return products
    return products.filter((p) =>
      productLabel(p).toLowerCase().includes(q) ||
      (p.product_id || '').toLowerCase().includes(q),
    )
  }, [products, search])

  const toggle = (code) => {
    setSavedAt(null)
    setSelected((cur) =>
      cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code],
    )
  }

  const handleSave = async () => {
    if (!selectedId) return
    setSaving(true)
    setError(null)
    try {
      const res = await updateProductCertifications(selectedId, selected)
      setSelected(res.certifications || [])
      setSavedAt(new Date())
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="admin-aim-subtab-body">
      <header className="admin-aim-header">
        <div>
          <h2 className="admin-aim-title">Certifications</h2>
          <p className="admin-aim-sub">
            Maintain the standardized safety/compliance certifications each cell
            holds. Used by the assistant as a hard filter during cell selection.
          </p>
        </div>
      </header>

      {productError && <div className="admin-aim-error-banner">{productError}</div>}

      <div className="admin-aim-toolbar">
        <input
          type="search"
          className="admin-aim-search"
          placeholder="Search products…"
          value={search}
          onInput={(e) => setSearch(e.currentTarget.value)}
        />
        <label className="admin-aim-field">
          <select
            className="admin-aim-input"
            value={selectedId || ''}
            onChange={(e) => setSelectedId(e.currentTarget.value || null)}
            aria-label="Select product"
          >
            <option value="">
              {loadingProducts ? 'Loading products…' : 'Select a product…'}
            </option>
            {filtered.map((p) => (
              <option key={p.product_id} value={p.product_id}>
                {productLabel(p)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {!selectedId ? (
        <p className="admin-aim-empty">Pick a product to edit its certifications.</p>
      ) : loadingCerts ? (
        <p className="admin-aim-empty">Loading certifications…</p>
      ) : (
        <div className="admin-flow-options">
          <div className="admin-flow-options__label">Certifications</div>
          {allowed.length === 0 ? (
            <p className="admin-aim-empty">No certification codes configured.</p>
          ) : (
            <div className="admin-aim-segmented" role="group" aria-label="Certification codes">
              {allowed.map((code) => {
                const on = selected.includes(code)
                return (
                  <button
                    key={code}
                    type="button"
                    className={`admin-aim-seg ${on ? 'is-active' : ''}`}
                    aria-pressed={on}
                    onClick={() => toggle(code)}
                  >
                    {on ? '✓ ' : ''}
                    {code}
                  </button>
                )
              })}
            </div>
          )}

          {error && <div className="admin-aim-error">{error}</div>}

          <div className="admin-aim-drawer-footer">
            {savedAt && (
              <span className="admin-aim-meta">
                Saved {savedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            <button
              type="button"
              className="admin-ai-btn admin-ai-btn--send"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save certifications'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
